import { db } from '../../db/database';

export type SanitizedOrderRow = {
  orderId: string | null;
  orderNumber: string | null;
  claimCode: string | null;
  orderStatus: string | null;
  paymentStatus: string | null;
  createdAt: string | null;
};

export type LocalDataRecoveryReport = {
  databaseVersion: number | null;
  unavailableReason?: 'DATABASE_NOT_FOUND' | 'SAFE_READ_UNAVAILABLE' | 'SCHEMA_VERSION_MISMATCH';
  counts: { orders: number; payments: number; outbox: number };
  orders: SanitizedOrderRow[];
  payments: { relatedOrderId: string | null }[];
  outbox: {
    eventId: string | null;
    aggregateId: string | null;
    eventType: string | null;
    status: string | null;
    attempts: number | null;
    payloadOrder: SanitizedOrderRow | null;
  }[];
};

const emptyCounts = { orders: 0, payments: 0, outbox: 0 };

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function projectOrder(value: unknown): SanitizedOrderRow | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  return {
    orderId: stringOrNull(row['id']),
    orderNumber: stringOrNull(row['orderNumber']),
    claimCode: stringOrNull(row['claimCode']),
    orderStatus: stringOrNull(row['orderStatus']),
    paymentStatus: stringOrNull(row['paymentStatus']),
    createdAt: stringOrNull(row['createdAt']),
  };
}

function projectOutboxOrder(eventType: unknown, payload: unknown): SanitizedOrderRow | null {
  if (eventType !== 'ORDER_CREATED' || typeof payload !== 'string') return null;
  try {
    const parsed: unknown = JSON.parse(payload);
    if (!parsed || typeof parsed !== 'object') return null;
    return projectOrder((parsed as Record<string, unknown>)['order']);
  } catch {
    return null;
  }
}

export async function readLocalDataRecoveryReport(): Promise<LocalDataRecoveryReport> {
  if (typeof indexedDB.databases !== 'function') {
    return {
      databaseVersion: null,
      unavailableReason: 'SAFE_READ_UNAVAILABLE',
      counts: emptyCounts,
      orders: [],
      payments: [],
      outbox: [],
    };
  }

  let databases: IDBDatabaseInfo[];
  try {
    databases = await indexedDB.databases();
  } catch {
    return {
      databaseVersion: null,
      unavailableReason: 'SAFE_READ_UNAVAILABLE',
      counts: emptyCounts,
      orders: [],
      payments: [],
      outbox: [],
    };
  }

  const existing = databases.find((database) => database.name === db.name);
  if (!existing || existing.version === undefined) {
    return {
      databaseVersion: existing?.version ?? null,
      unavailableReason: 'DATABASE_NOT_FOUND',
      counts: emptyCounts,
      orders: [],
      payments: [],
      outbox: [],
    };
  }

  // Dexie stores schema version 7 as native IndexedDB version 70. Refuse to
  // open unless that native version matches, so this read cannot upgrade it.
  const expectedNativeVersion = Math.round(db.verno * 10);
  const dexieSchemaVersion = existing.version / 10;
  if (existing.version !== expectedNativeVersion) {
    return {
      databaseVersion: dexieSchemaVersion,
      unavailableReason: 'SCHEMA_VERSION_MISMATCH',
      counts: emptyCounts,
      orders: [],
      payments: [],
      outbox: [],
    };
  }

  try {
    return await db.transaction('r', [db.orders, db.payments, db.outbox], async () => {
      const [orders, payments, outbox] = await Promise.all([
        db.orders.toArray(),
        db.payments.toArray(),
        db.outbox.toArray(),
      ]);

      return {
        databaseVersion: dexieSchemaVersion,
        counts: {
          orders: orders.length,
          payments: payments.length,
          outbox: outbox.length,
        },
        orders: orders.map(projectOrder).filter((row): row is SanitizedOrderRow => row !== null),
        payments: payments.map((payment) => ({ relatedOrderId: stringOrNull(payment.orderId) })),
        outbox: outbox.map((event) => ({
          eventId: stringOrNull(event.id),
          aggregateId: stringOrNull(event.aggregateId),
          eventType: stringOrNull(event.eventType),
          status: stringOrNull(event.status),
          attempts: Number.isFinite(event.attempts) ? event.attempts : null,
          payloadOrder: projectOutboxOrder(event.eventType, event.payload),
        })),
      };
    });
  } catch {
    return {
      databaseVersion: dexieSchemaVersion,
      unavailableReason: 'SAFE_READ_UNAVAILABLE',
      counts: emptyCounts,
      orders: [],
      payments: [],
      outbox: [],
    };
  }
}
